/**
 * Upload Monitoring Sagas
 *
 * Background sagas for monitoring:
 * - Network connection (online/offline events)
 * - Page visibility (tab switching)
 * - Upload progress polling
 *
 * @module upload-monitoring
 */

import { put, take, delay, fork } from 'redux-saga/effects'
import { eventChannel } from 'redux-saga'
import uploadState from './upload-state'
import { UPLOAD_CONFIG, TIMING, LOG_EMOJI } from './upload-config'
import {
  mediaVideoStreamingUploadProgressAction,
  mediaVideoStreamingUploadCancelAction,
} from '../../reducers/media-video/action'
import { operationSuccess } from '../../reducers/operation/action'
import { mediaDirectoryAddVideo } from '../../reducers/media-directory/action'
import { MEDIA_VIDEO_STREAMING_UPLOAD_START } from '../../reducers/media-video/type'

/**
 * Network monitoring saga
 * Detects online/offline events and handles auto-resume
 */
export function* networkMonitoringSaga() {
  // Set up event listeners for network changes
  const onlineChannel = eventChannel(emitter => {
    const handleOnline = () => {
      uploadState.setNetworkOnline(true)
      emitter({ type: 'ONLINE' })
    }

    const handleOffline = () => {
      uploadState.setNetworkOnline(false)
      emitter({ type: 'OFFLINE' })
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Initial state check
    if (!navigator.onLine && uploadState.isNetworkOnline()) {
      handleOffline()
    } else if (navigator.onLine && !uploadState.isNetworkOnline()) {
      handleOnline()
    }

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  })

  try {
    while (true) {
      const event = yield take(onlineChannel)

      if (event.type === 'ONLINE') {
        // Connection restored

        // Wait for connection to stabilize
        yield delay(UPLOAD_CONFIG.AUTO_RESUME_DELAY)

        // Update all paused uploads to show they're resuming
        const pausedUploads = Array.from(uploadState.paused)
        for (const uploadId of pausedUploads) {
          const progress = uploadState.getProgress(uploadId)
          if (progress) {
            uploadState.setProgress(uploadId, {
              ...progress,
              message: 'Connection restored. Resuming upload...',
              isPaused: false,
            })
          }
        }

        // Clear paused uploads
        uploadState.clearPaused()

        // Restart uploads that were waiting for network
        const waitingQueueSize = uploadState.getWaitingQueueSize()
        if (waitingQueueSize > 0) {
          const uploadsToRestart = uploadState.getWaitingQueueEntries()

          for (const [uploadId, uploadData] of uploadsToRestart) {
            // Update progress to show restarting
            uploadState.setProgress(uploadId, {
              message: 'Connection restored. Restarting upload...',
              percentComplete: 0,
              isRestarting: true,
            })

            // Remove from waiting map
            uploadState.removeFromWaitingQueue(uploadId)

            yield put({
              type: MEDIA_VIDEO_STREAMING_UPLOAD_START,
              payload: {
                file: uploadData.file,
                uploadId: uploadData.uploadId,
                parentFolderId: uploadData.parentFolderId,
              },
            })
          }
        }
      } else if (event.type === 'OFFLINE') {
        // Connection lost

        // Mark active uploads as paused
        const activeUploadIds = uploadState.getActiveUploadIds()
        for (const uploadId of activeUploadIds) {
          uploadState.markAsPaused(uploadId)

          const progress = uploadState.getProgress(uploadId)
          uploadState.setProgress(uploadId, {
            ...progress,
            message:
              'No internet connection. Upload will resume when connected.',
            isPaused: true,
          })
        }
      }
    }
  } finally {
    onlineChannel.close()
  }
}

/**
 * Page Visibility monitoring saga
 * Detects when tab becomes hidden/visible and warns about potential issues
 */
export function* pageVisibilityMonitoringSaga() {
  // Set up event listeners for visibility changes
  const visibilityChannel = eventChannel(emitter => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden

      if (isVisible) {
        uploadState.setPageVisible(true)

        // Calculate how long tab was hidden
        const hiddenDuration = uploadState.getHiddenDuration()
        if (hiddenDuration) {
          // Warn if tab was hidden for a long time
          if (hiddenDuration > TIMING.LONG_HIDDEN_THRESHOLD) {
            console.warn(
              `${LOG_EMOJI.WARNING} Tab was hidden for ${Math.round(
                hiddenDuration / 1000,
              )}s - some uploads may have been affected`,
            )
          }
        }

        emitter({ type: 'VISIBLE' })
      } else {
        uploadState.setPageVisible(false)

        // Warn about active uploads
        const activeUploadIds = uploadState.getActiveUploadIds()
        if (activeUploadIds.length > 0) {
          console.warn(
            `${LOG_EMOJI.WARNING} Tab hidden with ${activeUploadIds.length} active upload(s) - browser may throttle uploads`,
          )
        }

        emitter({ type: 'HIDDEN' })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Initial state check
    if (!document.hidden && !uploadState.isPageVisible()) {
      handleVisibilityChange()
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  })
}

/**
 * Progress monitoring saga
 * Polls for progress updates and dispatches to Redux
 */
export function* progressMonitoringSaga() {
  while (true) {
    // Check for progress updates every 100ms
    yield delay(TIMING.PROGRESS_POLL_INTERVAL)

    const allProgress = uploadState.getAllProgress()
    const progressEntries = Object.entries(allProgress)

    if (progressEntries.length > 0) {
      for (const [uploadId, progress] of progressEntries) {
        // Dispatch progress update to Redux
        yield put(mediaVideoStreamingUploadProgressAction(uploadId, progress))

        // Check if processing is complete
        const isCompleted = (progress.status === 'completed' || progress.status === 'available') && uploadState.isComplete(uploadId)
        if (isCompleted) {
          console.log(`${LOG_EMOJI.SUCCESS} Video upload completed!`, {
            uploadId,
            status: progress.status,
            details: progress.details
          })

          // Dispatch operation success
          yield put(operationSuccess())

          // Add video details to the list if available
          if (progress.details) {
            console.log(`${LOG_EMOJI.SUCCESS} Adding video to list from details:`, progress.details)
            yield put(mediaDirectoryAddVideo(progress.details))
          }

          // Clean up completion flags
          uploadState.deleteComplete(uploadId)
          uploadState.deleteProgress(uploadId)

          // Immediately remove upload from Redux (no delay)
          yield put(mediaVideoStreamingUploadCancelAction(uploadId))

          console.log(`${LOG_EMOJI.SUCCESS} Video added to list successfully!`)
        }
      }

      // Clear processed progress
      uploadState.clearAllProgress()
    }
  }
}
